import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Undo2 } from "lucide-react";
import { fmtDateTime } from "@/lib/format";

export function ImportHistory() {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({
    queryKey: ["recon-imports"],
    queryFn: async () => (await api.get("/reconciliation/imports")).data,
  });
  const undo = useMutation({
    mutationFn: async (id) => (await api.post(`/reconciliation/imports/${id}/undo`)).data,
    onSuccess: (r) => {
      toast.success(`Importazione annullata: ${r.donazioni_rimosse} donazioni rimosse, ${r.importi_ripristinati} importi ripristinati, ${r.donatori_rimossi} donatori rimossi`);
      qc.invalidateQueries({ queryKey: ["recon-imports"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const lastActive = items.find((i) => i.status === "attiva");
  return (
    <Card className="border border-stone-200 rounded-xl shadow-none" data-testid="recon-history">
      <div className="p-4 border-b border-stone-100 text-sm font-medium">Importazioni effettuate</div>
      {items.length === 0 ? <div className="p-8 text-center text-sm text-stone-400">Nessuna importazione ancora.</div> : (
        <ul className="divide-y divide-stone-100">
          {items.map((i) => (
            <li key={i.id} className="px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`recon-import-${i.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="text-sm font-medium text-[#1C352D]">Mese {i.month} · {i.filename}</div>
                <div className="text-[11px] text-stone-500">
                  {fmtDateTime(i.created_at)} · {i.created_by_name} · {i.stats.donazioni_aggiunte} donazioni (€ {i.stats.totale}), {i.stats.donatori_creati} nuovi donatori, {i.stats.importi_corretti} correzioni
                </div>
              </div>
              {i.status === "annullata"
                ? <span className="text-[11px] rounded-full bg-stone-100 text-stone-500 px-2 py-0.5">Annullata {i.undone_by_name ? `da ${i.undone_by_name}` : ""}</span>
                : i.id === lastActive?.id && (
                  <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid={`recon-undo-${i.id}`}
                    onClick={() => window.confirm("Annullare questa importazione? Verranno cancellate le donazioni e i donatori creati e ripristinati gli importi corretti.") && undo.mutate(i.id)}>
                    <Undo2 size={13} className="mr-1"/> Annulla importazione
                  </Button>
                )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
